(() => {
  'use strict';

  const i18n = window.CV6_PDP_I18N || {};
  const clean = value => String(value || '')
    .replace(/\s*\u2014\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/^[✓✔•·\-–]+\s*/, '')
    .trim();

  const shorten = (value, limit = 82) => {
    const text = clean(value);
    if (text.length <= limit) return text;
    const slice = text.slice(0, limit + 1);
    const cut = slice.lastIndexOf(' ');
    return `${slice.slice(0, cut > limit * .6 ? cut : limit).trim()}…`;
  };

  const key = value => clean(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const iconFor = text => {
    const value = key(text);
    if (/geur|fris|parfum|weken/.test(value)) return '🌸';
    if (/vegan|dierproef/.test(value)) return '🐰';
    if (/microplastic|plasticvrij|milieu|pva|afbreek|bewust|plant/.test(value)) return '🌿';
    if (/nederland|velsen|eigen fabriek|gemaakt/.test(value)) return '🇳🇱';
    if (/vlek|enzym|waskracht|krachtig|schoon/.test(value)) return '✨';
    if (/navul|compact|verpakking|fles|ruimte/.test(value)) return '♻️';
    if (/temperatuur|dose|wasbeurt|oplos|water/.test(value)) return '💧';
    if (/huid|mild|zacht|baby/.test(value)) return '🤍';
    return '✓';
  };

  const PRODUCT_ID_HANDLES = {
    '15097610731897': 'wasmiddel-wasstrips-g6',
    '10867957629255': 'wasmiddel-wasstrips-g6',
    '7321860309173': 'wasstrips',
    '8955664564551': 'geurparels-geurbooster-wasparfum',
    '9947294204231': 'geurparels-geurbooster-wasparfum',
    '9467584119111': 'wasmachine-diepreinigingstabletten',
    '9830212993351': 'wasmachine-diepreinigingstabletten',
    '8578515145031': 'proefpakket-alle-geuren-lentefris-lavendel-zeebries-wilde-bossen-kersenbloesem-wolkendek',
    '8868721590599': 'proefpakket-alle-geuren-lentefris-lavendel-zeebries-wilde-bossen-kersenbloesem-wolkendek',
    '8703991771463': 'wasparfum',
    '10526377673031': 'wasparfum',
    '9507150496071': 'vaatwasser-diepreinigingstabletten',
    '9830221381959': 'vaatwasser-diepreinigingstabletten',
    '8592480272711': 'vaatwasstrips',
    '8937577513287': 'vaatwastabletten',
    '8851692880199': 'cosmeau-toiletreiniger',
    '7399719370933': 'cosmeau-toiletreiniger',
    '9598799253847': 'wasmiddel-wasstrips-voor-donkere-was',
    '8860093317447': 'wasmiddel-wasstrips-voor-donkere-was',
    '9598789357911': 'wasmiddel-voor-witte-was',
    '8860091154759': 'wasmiddel-voor-witte-was',
    '8399241703751': 'cleaning-tabs-starterkit',
    '7402286383285': 'cleaning-tabs-starterkit',
    '10329736184135': 'solid-parfums'
  };

  const PRODUCT_SNAPSHOT_OVERRIDES = {
    'wasmiddel-wasstrips-g6': {
      'nl': { benefits: ['5 enzymen pakken hardnekkig vuil aan', '71% meer actieve stof per strip dan G5', 'Meestal een halve tot hele strip minder nodig', '100 strips in één compacte verpakking'], summary: 'Geconcentreerde G6 wasstrips voor krachtig wassen met minder product.' },
      'en': { benefits: ['5 enzymes target stubborn dirt', '71% more active ingredients per strip than G5', 'Usually half to one strip less is needed', '100 sheets in one compact pack'], summary: 'Concentrated G6 laundry strips for powerful cleaning with less product.' },
      'de': { benefits: ['5 Enzyme lösen hartnäckigen Schmutz', '71% mehr Wirkstoff pro Streifen als G5', 'Meist ein halber bis ganzer Streifen weniger nötig', '100 Streifen in einer kompakten Packung'], summary: 'Konzentrierte G6-Waschstreifen für starke Reinigung mit weniger Produkt.' }
    },
    'wasstrips': {
      'nl': { benefits: ['Wast schoon vanaf 15 °C', 'Een halve strip voor een kleine was', 'Compact en voorgedoseerd zonder fles', 'Kies 20 tot 360 wasbeurten per verpakking'], summary: 'Lichtgewicht wasstrips voor een schone was zonder vloeibaar wasmiddel.' },
      'en': { benefits: ['Cleans from 15 °C', 'Half a strip for a small load', 'Compact and pre-dosed without a bottle', 'Choose 20 to 360 washes per pack'], summary: 'Lightweight laundry strips for clean clothes without liquid detergent.' },
      'de': { benefits: ['Reinigt ab 15 °C', 'Ein halber Streifen für eine kleine Wäsche', 'Kompakt und vordosiert ohne Flasche', 'Wähle 20 bis 360 Waschgänge pro Packung'], summary: 'Leichte Waschstreifen für saubere Wäsche ohne Flüssigwaschmittel.' }
    },
    'geurparels-geurbooster-wasparfum': {
      'nl': { benefits: ['Frisse geurboost voor iedere was', 'Kies uit 9 geuren en Pure', 'Doseer zelf van subtiel tot krachtig', 'Navullen zonder telkens een nieuwe tin'], summary: 'Geurparels voor een doseerbare geurboost direct in de trommel.' },
      'en': { benefits: ['A fresh scent boost for every wash', 'Choose from 9 scents and Pure', 'Dose from subtle to strong', 'Refill without buying a new tin each time'], summary: 'Scent pearls for an adjustable fragrance boost directly in the drum.' },
      'de': { benefits: ['Frischer Duftboost für jede Wäsche', 'Wähle aus 9 Düften und Pure', 'Von dezent bis intensiv dosierbar', 'Nachfüllen ohne jedes Mal eine neue Dose'], summary: 'Duftperlen für einen dosierbaren Duftboost direkt in der Trommel.' }
    },
    'wasmachine-diepreinigingstabletten': {
      'nl': { benefits: ['Lost kalk, vet en zeepresten op', 'Reinigt ook leidingen en verwarmingselement', 'Eén tablet per maand', '12 tabletten voor een jaar onderhoud'], summary: 'Voorgedoseerde dieptereiniging voor een frisse, schone wasmachine.' },
      'en': { benefits: ['Dissolves limescale, grease and detergent residue', 'Cleans pipes and the heating element too', 'One tablet per month', '12 tablets for a year of maintenance'], summary: 'Pre-dosed deep cleaning for a fresh, clean washing machine.' },
      'de': { benefits: ['Löst Kalk, Fett und Waschmittelreste', 'Reinigt auch Leitungen und Heizelement', 'Eine Tablette pro Monat', '12 Tabletten für ein Jahr Pflege'], summary: 'Vordosierte Tiefenreinigung für eine frische, saubere Waschmaschine.' }
    },
    'proefpakket-alle-geuren-lentefris-lavendel-zeebries-wilde-bossen-kersenbloesem-wolkendek': {
      'nl': { benefits: ['Probeer zes geuren in één pakket', '120 wasbeurten met 6 pakken', 'Wast schoon vanaf 15 °C', 'Wissel geur per wasmoment'], summary: 'Zes wasstripgeuren om thuis te ontdekken en af te wisselen.' },
      'en': { benefits: ['Try six scents in one bundle', '120 washes across 6 packs', 'Cleans from 15 °C', 'Switch scent with each wash'], summary: 'Six laundry-strip scents to discover and alternate at home.' },
      'de': { benefits: ['Sechs Düfte in einem Paket testen', '120 Waschgänge mit 6 Packungen', 'Reinigt ab 15 °C', 'Duft bei jeder Wäsche wechseln'], summary: 'Sechs Waschstreifendüfte zum Entdecken und Abwechseln.' }
    },
    'wasparfum': {
      'nl': { benefits: ['Geurboost die je zelf doseert', 'Kies uit 9 geuren en Pure', '250 ml dispenser of 500 ml navulling', 'Direct in het wasverzachterbakje'], summary: 'Geconcentreerde wasparfum voor een geursterkte die bij jou past.' },
      'en': { benefits: ['A scent boost you dose yourself', 'Choose from 9 scents and Pure', '250 ml dispenser or 500 ml refill', 'Add directly to the softener compartment'], summary: 'Concentrated laundry perfume with a fragrance strength that suits you.' },
      'de': { benefits: ['Duftboost zum selbst Dosieren', 'Wähle aus 9 Düften und Pure', '250-ml-Spender oder 500-ml-Nachfüllung', 'Direkt ins Weichspülerfach'], summary: 'Konzentriertes Wäscheparfum mit einer Duftstärke, die zu dir passt.' }
    },
    'vaatwasser-diepreinigingstabletten': {
      'nl': { benefits: ['Pakt vet, kalk en verborgen vuil aan', 'Reinigt filters, sproeiarmen en leidingen', 'Eén tablet per maand', '12 tabletten voor een jaar onderhoud'], summary: 'Een grondige onderhoudsbeurt voor je lege vaatwasser.' },
      'en': { benefits: ['Targets grease, limescale and hidden dirt', 'Cleans filters, spray arms and pipes', 'One tablet per month', '12 tablets for a year of maintenance'], summary: 'A thorough maintenance clean for your empty dishwasher.' },
      'de': { benefits: ['Löst Fett, Kalk und verborgenen Schmutz', 'Reinigt Filter, Sprüharme und Leitungen', 'Eine Tablette pro Monat', '12 Tabletten für ein Jahr Pflege'], summary: 'Eine gründliche Pflege für deine leere Spülmaschine.' }
    },
    'vaatwasstrips': {
      'nl': { benefits: ['Een halve strip voor een normale vaat', 'Lost op vanaf 30 °C', 'Compact en voorgedoseerd', '60 tot 120 vaatwasbeurten per pak'], summary: 'Compacte vaatwasstrips die krachtig reinigen zonder grote verpakking.' },
      'en': { benefits: ['Half a strip for a normal load', 'Dissolves from 30 °C', 'Compact and pre-dosed', '60 to 120 dishwasher cycles per pack'], summary: 'Compact dishwasher strips that clean powerfully without bulky packaging.' },
      'de': { benefits: ['Ein halber Streifen für normales Geschirr', 'Löst sich ab 30 °C auf', 'Kompakt und vordosiert', '60 bis 120 Spülgänge pro Packung'], summary: 'Kompakte Spülmaschinenstreifen für starke Reinigung ohne große Verpackung.' }
    },
    'vaatwastabletten': {
      'nl': { benefits: ['Enzymen pakken vet en etensresten aan', 'Glansspoelmiddel voor een heldere vaat', 'Voorgedoseerd, meestal één tablet per beurt', '50 vaatwasbeurten in een compacte verpakking'], summary: 'Krachtige 2-in-1 vaatwastabletten op plantaardige basis, compact en klaar voor gebruik.' },
      'en': { benefits: ['Enzymes target grease and food residue', 'Rinse aid for sparkling dishes', 'Pre-dosed, usually one tablet per cycle', '50 dishwasher cycles in one compact pack'], summary: 'Powerful plant-based 2-in-1 dishwasher tablets, compact and ready to use.' },
      'de': { benefits: ['Enzyme lösen Fett und Speisereste', 'Klarspüler für glänzendes Geschirr', 'Vordosiert, meist eine Tablette pro Spülgang', '50 Spülgänge in einer kompakten Packung'], summary: 'Kraftvolle 2-in-1-Spülmaschinentabs auf pflanzlicher Basis, kompakt und sofort einsatzbereit.' }
    },
    'cosmeau-toiletreiniger': {
      'nl': { benefits: ['Ontkalkt en verwijdert vuil', 'Een halve strip voor normale reiniging', 'Lost snel op in het toiletwater', 'Tot 40 schoonmaakbeurten per verpakking'], summary: 'Compacte toiletreinigerstrips die oplossen in het water.' },
      'en': { benefits: ['Removes limescale and dirt', 'Half a strip for regular cleaning', 'Dissolves quickly in toilet water', 'Up to 40 cleans per pack'], summary: 'Compact toilet-cleaning strips that dissolve in water.' },
      'de': { benefits: ['Entfernt Kalk und Schmutz', 'Ein halber Streifen für normale Reinigung', 'Löst sich schnell im Toilettenwasser auf', 'Bis zu 40 Reinigungen pro Packung'], summary: 'Kompakte WC-Reinigungsstreifen, die sich im Wasser auflösen.' }
    },
    'wasmiddel-wasstrips-voor-donkere-was': {
      'nl': { benefits: ['Reinigt donkere en zwarte was', 'Helpt kleuren langer mooi te houden', 'Een halve strip voor een kleine was', 'Tot 80 wasbeurten per verpakking'], summary: 'Voorgedoseerde wasstrips speciaal voor donkere en zwarte kleding.' },
      'en': { benefits: ['Cleans dark and black laundry', 'Helps colours stay beautiful longer', 'Half a strip for a small load', 'Up to 80 washes per pack'], summary: 'Pre-dosed laundry strips specially made for dark and black clothing.' },
      'de': { benefits: ['Reinigt dunkle und schwarze Wäsche', 'Hilft, Farben länger schön zu halten', 'Ein halber Streifen für eine kleine Wäsche', 'Bis zu 80 Waschgänge pro Packung'], summary: 'Vordosierte Waschstreifen speziell für dunkle und schwarze Kleidung.' }
    },
    'wasmiddel-voor-witte-was': {
      'nl': { benefits: ['Reinigt witte en lichte was', 'Helpt wit langer helder te houden', 'Een halve strip voor een kleine was', 'Tot 80 wasbeurten per verpakking'], summary: 'Voorgedoseerde wasstrips speciaal voor witte en lichte kleding.' },
      'en': { benefits: ['Cleans white and light laundry', 'Helps whites stay bright longer', 'Half a strip for a small load', 'Up to 80 washes per pack'], summary: 'Pre-dosed laundry strips specially made for white and light clothing.' },
      'de': { benefits: ['Reinigt weiße und helle Wäsche', 'Hilft, Weiß länger strahlend zu halten', 'Ein halber Streifen für eine kleine Wäsche', 'Bis zu 80 Waschgänge pro Packung'], summary: 'Vordosierte Waschstreifen speziell für weiße und helle Kleidung.' }
    },
    'cleaning-tabs-starterkit': {
      'nl': { benefits: ['Vier schoonmakers in één starterskit', 'Herbruikbare glazen sprayflessen', 'Eén tablet per fles van ongeveer 500 ml', 'Navullen met water en een nieuwe tablet'], summary: 'Een complete schoonmaakset met herbruikbare flessen en oplosbare tabletten.' },
      'en': { benefits: ['Four cleaners in one starter kit', 'Reusable glass spray bottles', 'One tablet per bottle of about 500 ml', 'Refill with water and a new tablet'], summary: 'A complete cleaning set with reusable bottles and dissolvable tablets.' },
      'de': { benefits: ['Vier Reiniger in einem Starterset', 'Wiederverwendbare Glassprühflaschen', 'Eine Tablette pro Flasche mit etwa 500 ml', 'Mit Wasser und neuer Tablette nachfüllen'], summary: 'Ein komplettes Reinigungsset mit wiederverwendbaren Flaschen und löslichen Tabletten.' }
    },
    'wasstrips-bewaardoos-box': {
      'nl': { benefits: ['Houdt je wasstrips droog', 'Helpt de geur langer te behouden', 'Scharnierdeksel, makkelijk met één hand', 'Stapelbaar met handig doorkijkvenster'], summary: 'Een metalen bewaarbox die je strips droog, geurig en netjes opgeborgen houdt.' },
      'en': { benefits: ['Keeps your laundry strips dry', 'Helps preserve their fragrance longer', 'Hinged lid for easy one-handed use', 'Stackable with a useful viewing window'], summary: 'A metal storage box that keeps your strips dry, fragrant and neatly stored.' },
      'de': { benefits: ['Hält deine Waschstreifen trocken', 'Hilft, den Duft länger zu bewahren', 'Klappdeckel für einfache Einhandbedienung', 'Stapelbar mit praktischem Sichtfenster'], summary: 'Eine Metallbox, die deine Streifen trocken, duftend und ordentlich aufbewahrt.' }
    },
    'drogerballen-6-stuks-met-bewaardoos': {
      'nl': { benefits: ['Tot 25% kortere droogtijd', '100% natuurlijke Nieuw-Zeelandse wol', 'Minder kreukels en statische lading', 'RWS-gecertificeerd met vilten bewaardoos'], summary: 'Zes natuurlijke wollen drogerballen voor sneller, zachter en minder statisch wasgoed.' },
      'en': { benefits: ['Up to 25% shorter drying time', '100% natural New Zealand wool', 'Fewer creases and less static', 'RWS-certified with a felt storage box'], summary: 'Six natural wool dryer balls for faster, softer laundry with less static.' },
      'de': { benefits: ['Bis zu 25% kürzere Trockenzeit', '100% natürliche neuseeländische Wolle', 'Weniger Falten und statische Aufladung', 'RWS-zertifiziert mit Filz-Aufbewahrungsbox'], summary: 'Sechs natürliche Woll-Trocknerbälle für schnellere, weichere Wäsche mit weniger Statik.' }
    },
    'solid-parfums': {
      'nl': { benefits: ['100% microplasticvrij', 'Alcoholvrij', 'Navulbaar'], summary: 'Parfum in vaste vorm.' },
      'en': { benefits: ['100% microplastic-free', 'Alcohol-free', 'Refillable'], summary: 'Perfume in solid form.' },
      'de': { benefits: ['100% mikroplastikfrei', 'Alkoholfrei', 'Nachfüllbar'], summary: 'Parfüm in fester Form.' }
    }
  };

  const sentenceSummary = value => {
    const text = clean(value).replace(/\s*\.\.\.$/, '…');
    if (!text) return '';
    const matches = text.match(/[^.!?…]+[.!?…]+/g) || [];
    const summary = matches.length ? matches.slice(0, 2).join(' ') : text;
    return shorten(summary, 235);
  };

  const benefitCandidates = target => {
    const candidates = [];
    const corpus = clean(target.textContent);
    const addProof = (condition, text, score, order) => {
      if (condition) candidates.push({ text, score, order });
    };
    addProof(/100\s*%\s*microplasticvrij/i.test(corpus), '100% microplasticvrij', 64, -5);
    addProof(/tot\s+(?:wel\s+)?\d+\s+weken/i.test(corpus), (corpus.match(/tot\s+(?:wel\s+)?\d+\s+weken(?:\s+(?:lang\s+)?(?:fris|frisheid|geur))?/i) || [''])[0], 62, -4);
    addProof(/\bvegan\b/i.test(corpus), 'Vegan', 60, -3);
    addProof(/in nederland geproduceerd|gemaakt in nederland|nederland.{0,70}eigen fabriek|eigen fabriek.{0,70}nederland/i.test(corpus), 'Geproduceerd in Nederland, in onze eigen fabriek', 58, -2);
    const positive = /voordeel|waarom|bijzonder|kracht|minder|meer|zonder|vrij|vegan|compact|navul|fris|geur|weken|zacht|mild|temperatuur|dose|wasbeurt|oplos|gemaakt|fabriek|herbruik|bespaar|effectief|handig|ruimte|vlek|enzym/i;
    const negativeTopic = /ingredien|allerg|waarschu|veelgestelde|faq|inhoudsopgave/i;

    target.querySelectorAll('li').forEach((node, index) => {
      const text = clean(node.textContent);
      if (text.length < 8 || text.length > 190 || /\?$/.test(text)) return;
      const topic = node.closest('.cv6-desc-topic');
      const topicTitle = clean(topic?.querySelector('summary')?.textContent);
      const list = node.closest('ul,ol');
      const context = clean(`${topicTitle} ${list?.previousElementSibling?.textContent || ''}`);
      if (negativeTopic.test(topicTitle) && !positive.test(text)) return;
      let score = 20 - Math.min(index, 16) * .25;
      if (/voordeel|waarom|bijzonder|op een rij/i.test(context)) score += 18;
      if (positive.test(text)) score += 9;
      if (/^(giet|gebruik|voeg|strooi|doe|plaats|leg|schud|breng|draai|was|kies|sluit|bewaar)\b/i.test(text)) score -= 32;
      if (/\b(dop|trommel|doseerbak|stap \d|lijnen om te doseren)\b/i.test(text)) score -= 14;
      if (negativeTopic.test(topicTitle)) score -= 16;
      if ((text.match(/,/g) || []).length > 5) score -= 15;
      candidates.push({ text, score, order: index });
    });

    target.querySelectorAll('.cv6-desc-topic__body > p').forEach((node, index) => {
      const text = sentenceSummary(node.textContent);
      if (text.length < 18 || !positive.test(text)) return;
      const topicTitle = clean(node.closest('.cv6-desc-topic')?.querySelector('summary')?.textContent);
      if (negativeTopic.test(topicTitle)) return;
      candidates.push({ text, score: 8 - index * .1, order: 100 + index });
    });

    target.querySelectorAll('.cv6-desc-topic').forEach((node, index) => {
      const title = clean(node.querySelector('summary')?.textContent).replace(/[+−]\s*$/, '').trim();
      if (!title || /introductie|ingredient|allerg|waarschu|faq|veelgestelde/i.test(title)) return;
      candidates.push({ text: title, score: 2 - index * .1, order: 200 + index });
    });

    const seen = new Set();
    return candidates
      .sort((a, b) => b.score - a.score || a.order - b.order)
      .filter(item => {
        const normalized = key(item.text).slice(0, 56);
        if (!normalized || [...seen].some(existing => existing.includes(normalized) || normalized.includes(existing))) return false;
        seen.add(normalized);
        return true;
      })
      .slice(0, 4)
      .map(item => shorten(item.text));
  };

  const descriptionTopics = (descriptionHtml, limit = 4) => {
    const template = document.createElement('template');
    template.innerHTML = String(descriptionHtml || '');
    const root = template.content;
    root.querySelectorAll('script,style,noscript,form,button,input,select,textarea,iframe,object,embed').forEach(node => node.remove());

    const topics = [];
    let current = { title: '', parts: [] };
    const pushCurrent = () => {
      const text = shorten(current.parts.join(' '), 420);
      if (text) topics.push({ title: current.title || i18n.productDescription || 'Product description', text });
      current = { title: '', parts: [] };
    };

    root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li').forEach(node => {
      const text = clean(node.textContent);
      if (!text) return;
      if (/^H[1-6]$/.test(node.tagName)) {
        if (current.parts.length) pushCurrent();
        current.title = shorten(text.replace(/[:：]\s*$/, ''), 72);
        return;
      }
      if (node.tagName === 'LI' && node.closest('p')) return;
      if (text.length >= 8) current.parts.push(text);
    });
    pushCurrent();

    if (!topics.length) {
      const text = shorten(clean(root.textContent), 420);
      if (text) topics.push({ title: i18n.productDescription || 'Product description', text });
    }
    return topics.slice(0, Math.max(1, limit));
  };

  const resolveSnapshot = ({ productId = '', productHandle = '', descriptionHtml = '', locale = 'nl', target = null } = {}) => {
    const localizedHandle = clean(productHandle).toLowerCase();
    const id = String(productId || '');
    const handle = PRODUCT_ID_HANDLES[id] || localizedHandle;
    const language = clean(locale).toLowerCase().split('-')[0] || 'nl';
    const snapshotGroup = PRODUCT_SNAPSHOT_OVERRIDES[handle];
    const solidAllowed = id === '10329736184135' || (!id && localizedHandle === 'solid-parfums');
    const snapshotOverride = handle === 'solid-parfums'
      ? (solidAllowed ? snapshotGroup?.[language] : undefined)
      : (snapshotGroup?.[language] || snapshotGroup?.nl);
    let descriptionTarget = target;
    if (!descriptionTarget && descriptionHtml) {
      const template = document.createElement('template');
      template.innerHTML = String(descriptionHtml);
      descriptionTarget = template.content;
    }
    const benefits = (snapshotOverride?.benefits || (descriptionTarget ? benefitCandidates(descriptionTarget) : [])).slice(0, 4);
    const descriptionText = descriptionTarget?.textContent || '';
    return {
      handle,
      benefits,
      icons: benefits.map(text => {
        const resolved = iconFor(text);
        return snapshotOverride && resolved === '✓' ? '✅' : resolved;
      }),
      summary: snapshotOverride?.summary || sentenceSummary(descriptionText),
      topics: descriptionTopics(descriptionHtml || descriptionTarget?.textContent || ''),
      hasOverride: Boolean(snapshotOverride)
    };
  };

  window.CosmeauProductSnapshot = Object.freeze({
    clean,
    iconFor,
    sentenceSummary,
    descriptionTopics,
    resolveSnapshot
  });
})();
